// src/pages/PatientSettingsPage.jsx
// Route: /patient/settings
// Patient can update name, email, their collection addresses, and set or
// change their password.
// Uses PUT /api/patients/{id}, GET /api/patient-auth/me,
// POST /api/patient-auth/change-password,
// GET/POST/PUT /api/patients/{id}/addresses,
// PUT /api/patients/{id}/notification-preferences, and
// GET/PATCH /api/me/language

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import { PatientAppShell } from '../components/PatientLayout';
import { usePatient } from '../context/PatientContext';
import api, { patientAPI } from '../services/api';
import HealthProfileForm from '../components/HealthProfileForm';
import SymptomLogSection from '../components/SymptomLogSection';
import PatientConfoundsTab from '../components/healthrecords/PatientConfoundsTab';
import PrivacyConsentSection from '../components/PrivacyConsentSection';
import PushNotificationsSection from '../components/PushNotificationsSection';
import InstallAppSection from '../components/InstallAppSection';
import { getLanguage, setLanguage } from '../services/languageAPI';
import { displayPatientCode } from '../utils/patientCode';
import {
  HEALTH_PROFILE_ANCHOR,
  SYMPTOM_LOG_ANCHOR,
} from '../utils/patientProfileCompleteness';

// Which in-page tab each section's anchor lives under — the profile-gate
// deep-links here (#health-profile, #symptom-log) expecting the target
// section to already be scrollable, so the hash has to pick the right tab
// BEFORE the scroll effect runs, not just the section id within it.
const CONFOUND_ANCHOR = 'confound-log';
const PRIVACY_ANCHOR = 'privacy';
// The bell links here when the patient has turned in-app notifications off.
const NOTIFICATIONS_ANCHOR = 'notifications';
// Ask Previu can send the patient straight to their language choice.
const LANGUAGE_ANCHOR = 'language';
const ANCHOR_TABS = {
  [HEALTH_PROFILE_ANCHOR]: 'health',
  [SYMPTOM_LOG_ANCHOR]: 'health',
  [CONFOUND_ANCHOR]: 'health',
  [PRIVACY_ANCHOR]: 'privacy',
  [NOTIFICATIONS_ANCHOR]: 'preferences',
  [LANGUAGE_ANCHOR]: 'preferences',
};

const SETTINGS_TABS = [
  { key: 'profile', label: 'Profile' },
  { key: 'preferences', label: 'Preferences' },
  { key: 'health', label: 'Health Info' },
  { key: 'privacy', label: 'Privacy & consent' },
];

const BLANK_ADDRESS = {
  addressType: 'HOME',
  street: '', city: '', state: '', zipCode: '',
  landmark: '', country: 'India', isDefault: false,
};

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

const inputStyle = (extra = {}) => ({
  width: '100%', boxSizing: 'border-box',
  border: '1.5px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: '11px 14px', fontSize: 14,
  fontFamily: 'var(--font-body)',
  color: 'var(--text-primary)',
  background: 'var(--bg-subtle)',
  outline: 'none',
  transition: 'border-color 0.2s',
  ...extra,
});

const labelStyle = {
  fontSize: 13, fontWeight: 500,
  color: 'var(--text-secondary)',
  display: 'block', marginBottom: 6,
};

function Section({ title, children, id }) {
  return (
    <div id={id} style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      padding: '24px 28px',
      marginBottom: 20,
      boxShadow: 'var(--shadow-sm)',
    }}>
      <div style={{
        fontSize: 15, fontWeight: 700,
        color: 'var(--text-primary)',
        marginBottom: 20,
        paddingBottom: 12,
        borderBottom: '1px solid var(--border)',
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

export default function PatientSettingsPage() {
  const { patient, refreshPatient, profile, refreshProfile, avatarUrl,
          preferredLanguage, setPreferredLanguage } = usePatient();
  const location = useLocation();

  // Lazy initializer so a deep link (e.g. the profile-gate's
  // #health-profile) lands on the right tab on first paint, not a flash of
  // "Profile" before the hash effect below corrects it.
  const [activeTab, setActiveTab] = useState(() => {
    const hash = (location.hash || '').replace('#', '');
    return ANCHOR_TABS[hash] || 'profile';
  });

  useEffect(() => {
    const hash = (location.hash || '').replace('#', '');
    if (ANCHOR_TABS[hash]) setActiveTab(ANCHOR_TABS[hash]);
  }, [location.hash]);

  // ── Profile photo ──────────────────────────────────────────────────────
  const AVATAR_MAX_MB = 5;
  const AVATAR_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const avatarInputRef = useRef(null);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [avatarMsg, setAvatarMsg] = useState({ type: '', text: '' });

  function handleAvatarPick() {
    avatarInputRef.current?.click();
  }

  async function handleAvatarChange(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file next time
    if (!file || !patient?.id) return;

    if (!AVATAR_ACCEPTED_TYPES.includes(file.type)) {
      setAvatarMsg({ type: 'error', text: 'Please upload a JPG, PNG, or WEBP image.' });
      return;
    }
    if (file.size > AVATAR_MAX_MB * 1024 * 1024) {
      setAvatarMsg({ type: 'error', text: `Image too large — maximum ${AVATAR_MAX_MB}MB.` });
      return;
    }

    setAvatarSaving(true);
    setAvatarMsg({ type: '', text: '' });
    try {
      await patientAPI.uploadAvatar(patient.id, file);
      refreshProfile(); // hasAvatar flips true — PatientContext's own effect fetches the new image
      setAvatarMsg({ type: 'success', text: 'Photo updated.' });
    } catch (err) {
      setAvatarMsg({ type: 'error', text: err.response?.data?.message || 'Could not upload photo.' });
    } finally {
      setAvatarSaving(false);
    }
  }

  async function handleAvatarRemove() {
    if (!patient?.id) return;
    setAvatarSaving(true);
    setAvatarMsg({ type: '', text: '' });
    try {
      await patientAPI.deleteAvatar(patient.id);
      refreshProfile();
      setAvatarMsg({ type: 'success', text: 'Photo removed.' });
    } catch {
      setAvatarMsg({ type: 'error', text: 'Could not remove photo.' });
    } finally {
      setAvatarSaving(false);
    }
  }

  // Profile form
  const [profileForm, setProfileForm] = useState({
    firstName: patient?.firstName || '',
    lastName:  patient?.lastName  || '',
    email:     patient?.email     || '',
    // patient (login-cached) never carries gender at all; profile (the live
    // GET /api/patients/{id} record) is the real source of truth for it.
    gender:    profile?.gender    || patient?.gender || '',
  });

  // profile loads asynchronously after mount — backfill gender once it
  // arrives if the form doesn't already have a value from the line above.
  React.useEffect(() => {
    if (profile?.gender) {
      setProfileForm(f => (f.gender ? f : { ...f, gender: profile.gender }));
    }
  }, [profile]);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg,    setProfileMsg]    = useState({ type: '', text: '' });

  // Portal appearance — LIGHT (default, existing design) or DARK (new theme).
  const [theme,       setTheme]       = useState(profile?.theme || 'LIGHT');
  const [themeSaving, setThemeSaving] = useState(false);
  const [themeMsg,    setThemeMsg]    = useState({ type: '', text: '' });

  // Today screen layout — METRIC (default, the original screen) or SIMPLIFIED.
  const [todayView,       setTodayView]       = useState(profile?.todayView || 'METRIC');
  const [todayViewSaving, setTodayViewSaving] = useState(false);
  const [todayViewMsg,    setTodayViewMsg]    = useState({ type: '', text: '' });
  useEffect(() => {
    if (profile?.todayView) setTodayView(profile.todayView);
  }, [profile?.todayView]);

  React.useEffect(() => {
    if (profile?.theme) setTheme(profile.theme);
  }, [profile?.theme]);

  // Preferred language — held in PatientContext so Ask Previu can read it.
  // From the profile when it carries one, else GET /api/me/language once the
  // profile has loaded without it. Hidden altogether when the endpoint isn't
  // there (404 before backend #337 is deployed) or the GET fails, rather than
  // showing a control that can't save. languageAPI never ends the session.
  const [languageHidden, setLanguageHidden] = useState(false);
  const [languageSaving, setLanguageSaving] = useState(false);
  const [languageMsg,    setLanguageMsg]    = useState({ type: '', text: '' });
  const languageFetched = useRef(false);

  useEffect(() => {
    if (!profile || preferredLanguage || languageFetched.current) return;
    languageFetched.current = true;
    getLanguage().then(res => {
      if (res.ok) setPreferredLanguage(res.preferredLanguage);
      else setLanguageHidden(true);
    });
  }, [profile, preferredLanguage, setPreferredLanguage]);

  // Notification channels. Undefined = ON: the backend defaults both to true,
  // and a backend that predates these fields omits them entirely.
  const [notifPrefs, setNotifPrefs] = useState({
    whatsappNotificationPreference: profile?.whatsappNotificationPreference !== false,
    appNotificationPreference:      profile?.appNotificationPreference !== false,
  });
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifError,  setNotifError]  = useState('');

  React.useEffect(() => {
    setNotifPrefs({
      whatsappNotificationPreference: profile?.whatsappNotificationPreference !== false,
      appNotificationPreference:      profile?.appNotificationPreference !== false,
    });
  }, [profile?.whatsappNotificationPreference, profile?.appNotificationPreference]);

  // Password form
  const [pwForm, setPwForm] = useState({
    newPassword:     '',
    confirmPassword: '',
  });
  const [showPw,   setShowPw]   = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg,    setPwMsg]    = useState({ type: '', text: '' });

  // Whether this patient has a password at all.
  //
  // null means "not known yet" — either still loading, or the request failed.
  // The copy below falls back to wording that is true in both cases when it is
  // null, so a failed request degrades to the old hedged text rather than
  // asserting something wrong.
  //
  // GET /api/patient-auth/me is the only way to know: password_hash is
  // deliberately absent from every patient DTO, and the endpoint answers only
  // for the holder of the token (asking about someone else's number would be an
  // account-enumeration oracle).
  const [hasPassword, setHasPassword] = useState(null);

  React.useEffect(() => {
    const token = localStorage.getItem('previu_token');
    if (!token) return;
    let cancelled = false;
    fetch(`${API_BASE}/patient-auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (!cancelled && data && typeof data.hasPassword === 'boolean') {
          setHasPassword(data.hasPassword);
        }
      })
      .catch(() => { /* leave null — copy stays neutral */ });
    return () => { cancelled = true; };
  }, []);

  // ── Collection addresses ────────────────────────────────────────────────
  //
  // Added 21 Aug 2026: the patient portal had NO address UI at all. AddressPage
  // exists but is routed at /patients/:id/addresses behind PrivateRoute, which
  // is the staff route — a patient could not reach it, and PatientSettingsPage
  // had no address section. So the only way a patient could see or change where
  // their blood sample gets collected was to ask the chat assistant.
  //
  // That matters more than a normal missing-screen gap: the pincode on these
  // rows decides which slots Thyrocare offers AND where a phlebotomist is
  // physically sent. A patient could not check it was right.
  //
  // The API was already fully open to them — AddressController carries
  // @PreAuthorize("@patientAccess.isSelfOrStaff(#patientId, authentication)")
  // at class level, so all four verbs work for a patient on their own
  // addresses. This is UI only; no backend change was needed.
  const [addresses,     setAddresses]     = useState(null); // null = loading
  const [addrMsg,       setAddrMsg]       = useState({ type: '', text: '' });
  const [addrSaving,    setAddrSaving]    = useState(false);
  const [editingAddrId, setEditingAddrId] = useState(null); // 'new' | id | null
  const [addrForm,      setAddrForm]      = useState(BLANK_ADDRESS);

  const loadAddresses = React.useCallback(async () => {
    if (!patient?.id) return;
    try {
      const res = await api.get(`/patients/${patient.id}/addresses`);
      setAddresses(Array.isArray(res.data) ? res.data : []);
    } catch {
      setAddresses([]);
      setAddrMsg({ type: 'error', text: 'Could not load your addresses. Please reload the page.' });
    }
  }, [patient?.id]);

  React.useEffect(() => { loadAddresses(); }, [loadAddresses]);

  function startAddAddress() {
    setAddrForm(BLANK_ADDRESS);
    setEditingAddrId('new');
    setAddrMsg({ type: '', text: '' });
  }

  function startEditAddress(a) {
    setAddrForm({
      addressType: a.addressType || 'HOME',
      street:      a.street      || '',
      city:        a.city        || '',
      state:       a.state       || '',
      zipCode:     a.zipCode     || '',
      landmark:    a.landmark    || '',
      country:     a.country     || 'India',
      isDefault:   !!a.isDefault,
    });
    setEditingAddrId(a.id);
    setAddrMsg({ type: '', text: '' });
  }

  async function handleSaveAddress(e) {
    e.preventDefault();
    // street and pincode are the two fields a home visit cannot happen without,
    // so they are required here rather than left to a server error.
    if (!addrForm.street.trim()) {
      setAddrMsg({ type: 'error', text: 'Please enter the house/flat and street.' }); return;
    }
    if (!/^\d{6}$/.test(addrForm.zipCode.trim())) {
      setAddrMsg({ type: 'error', text: 'Pincode must be 6 digits.' }); return;
    }
    if (!addrForm.city.trim() || !addrForm.state.trim()) {
      setAddrMsg({ type: 'error', text: 'Please enter the city and state.' }); return;
    }
    setAddrSaving(true); setAddrMsg({ type: '', text: '' });
    try {
      const payload = { ...addrForm, zipCode: addrForm.zipCode.trim() };
      if (editingAddrId === 'new') {
        await api.post(`/patients/${patient.id}/addresses`, payload);
      } else {
        await api.put(`/patients/${patient.id}/addresses/${editingAddrId}`, payload);
      }
      setEditingAddrId(null);
      await loadAddresses();
      setAddrMsg({ type: 'success', text: 'Address saved.' });
    } catch (err) {
      setAddrMsg({
        type: 'error',
        text: err?.response?.data?.message || 'Could not save the address. Please try again.',
      });
    } finally { setAddrSaving(false); }
  }

  async function handleMakeDefault(a) {
    setAddrSaving(true); setAddrMsg({ type: '', text: '' });
    try {
      await api.put(`/patients/${patient.id}/addresses/${a.id}`, { ...a, isDefault: true });
      await loadAddresses();
      setAddrMsg({ type: 'success', text: 'Default collection address updated.' });
    } catch {
      setAddrMsg({ type: 'error', text: 'Could not update the default address.' });
    } finally { setAddrSaving(false); }
  }

  // ── Health profile data — loaded from API on mount ─────────────────────
  const [healthData,      setHealthData]      = useState(null);
  const [healthLoading,   setHealthLoading]   = useState(true);

  // Load current health data from backend
  React.useEffect(() => {
    if (!patient?.id) return;
    const token = localStorage.getItem('previu_token');
    fetch(`${API_BASE}/patients/${patient.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        setHealthData({
          medicalHistory:     data.medicalHistory     || '',
          allergies:          data.allergies           || '',
          currentMedications: data.currentMedications || '',
        });
      })
      .catch(() => setHealthData({}))
      .finally(() => setHealthLoading(false));
  }, [patient?.id]);

  const setProfile = k => e => setProfileForm(f => ({ ...f, [k]: e.target.value }));
  const setPw      = k => e => setPwForm(f => ({ ...f, [k]: e.target.value }));

  const initials = `${patient?.firstName?.[0] ?? ''}${patient?.lastName?.[0] ?? ''}`.toUpperCase();

  // ── Update profile ──────────────────────────────────────────────────────
  async function handleUpdateProfile(e) {
    e.preventDefault();
    if (!profileForm.firstName.trim() || !profileForm.lastName.trim()) {
      setProfileMsg({ type: 'error', text: 'First and last name are required.' }); return;
    }
    // DEF-023: gender is required on every current creation path — treat it
    // the same way here rather than letting a legacy record stay incomplete
    // forever because the save button never enforced it.
    if (!profileForm.gender) {
      setProfileMsg({ type: 'error', text: 'Gender is required.' }); return;
    }
    setProfileSaving(true); setProfileMsg({ type: '', text: '' });
    try {
      await api.put(`/patients/${patient.id}`, {
        firstName: profileForm.firstName.trim(),
        lastName:  profileForm.lastName.trim(),
        email:     profileForm.email.trim() || undefined,
        gender:    profileForm.gender,
      });
      // Update localStorage
      const updated = { ...patient,
        firstName: profileForm.firstName.trim(),
        lastName:  profileForm.lastName.trim(),
        email:     profileForm.email.trim(),
        gender:    profileForm.gender,
      };
      localStorage.setItem('previu_patient', JSON.stringify(updated));
      // Same-tab localStorage writes don't fire the 'storage' event, so without
      // this the context's `patient` stays stale for things like the header name.
      refreshPatient();
      // profile (not patient) is what the gender warnings actually check —
      // refetch it so this page and the shell's MissingGenderBanner drop the
      // warning immediately everywhere, without needing a reload.
      refreshProfile();
      setProfileMsg({ type: 'success', text: 'Profile updated successfully!' });
    } catch (err) {
      setProfileMsg({ type: 'error',
        text: err.response?.data?.message || 'Failed to update profile. Please try again.' });
    } finally { setProfileSaving(false); }
  }

  // ── Appearance (theme) ──────────────────────────────────────────────────
  async function handleThemeChange(next) {
    if (next === theme || themeSaving) return;
    setThemeSaving(true); setThemeMsg({ type: '', text: '' });
    try {
      await api.put(`/patients/${patient.id}`, { theme: next });
      setTheme(next);
      refreshProfile();
      setThemeMsg({ type: 'success', text: 'Appearance updated.' });
    } catch (err) {
      setThemeMsg({ type: 'error',
        text: err.response?.data?.message || 'Failed to update. Please try again.' });
    } finally { setThemeSaving(false); }
  }

  // ── Today screen layout ─────────────────────────────────────────────────
  async function handleTodayViewChange(next) {
    if (next === todayView || todayViewSaving) return;
    setTodayViewSaving(true); setTodayViewMsg({ type: '', text: '' });
    try {
      const res = await api.put(`/patients/${patient.id}`, { todayView: next });
      // The server answers with the saved patient. A backend that predates the
      // field accepts the request but silently drops it, so only report success
      // when the response confirms the value — otherwise the card would stay
      // selected and "Today screen updated." would show for a change that was
      // never stored (the choice would revert on the next profile load).
      if (res.data?.todayView !== next) {
        setTodayViewMsg({ type: 'error',
          text: 'Your choice could not be saved. Please try again in a moment.' });
        return;
      }
      setTodayView(next);
      refreshProfile();
      setTodayViewMsg({ type: 'success', text: 'Today screen updated.' });
    } catch (err) {
      setTodayViewMsg({ type: 'error',
        text: err.response?.data?.message || 'Failed to update. Please try again.' });
    } finally { setTodayViewSaving(false); }
  }

  // ── Language ────────────────────────────────────────────────────────────
  // The previous choice stays selected until the PATCH confirms the new one.
  // A 404 means the backend predates the endpoint, so the setting goes away
  // quietly; anything else (400, 401, 403, 5xx, network) shows the error.
  async function handleLanguageChange(next) {
    if (next === preferredLanguage || languageSaving) return;
    setLanguageSaving(true); setLanguageMsg({ type: '', text: '' });
    const res = await setLanguage(next);
    setLanguageSaving(false);
    if (res.ok) {
      setPreferredLanguage(res.preferredLanguage);
      refreshProfile();
      setLanguageMsg({ type: 'success', text: 'Language updated.' });
    } else if (res.status === 404) {
      setLanguageHidden(true);
    } else {
      setLanguageMsg({ type: 'error', text: res.message || 'Failed to update. Please try again.' });
    }
  }

  // ── Notification channels ──────────────────────────────────────────────
  // Saves on every checkbox change. The endpoint requires both fields, so the
  // unchanged one is sent with its current value. Optimistic: the checkbox
  // flips immediately and is rolled back if the save fails. refreshProfile()
  // afterwards is what makes the bell pick up the in-app setting without a
  // reload — it reads profile.appNotificationPreference from PatientContext.
  async function handleNotificationPreferenceChange(field, value) {
    if (notifSaving) return;
    const previous = notifPrefs;
    const body = { ...previous, [field]: value };
    setNotifError('');
    setNotifSaving(true);
    setNotifPrefs(body);
    try {
      await patientAPI.updateNotificationPreferences(patient.id, body);
      refreshProfile();
    } catch (err) {
      setNotifPrefs(previous);
      setNotifError(err.response?.data?.message || 'Failed to update notification preferences.');
    } finally { setNotifSaving(false); }
  }

  // ── Change password ─────────────────────────────────────────────────────
  async function handleChangePassword(e) {
    e.preventDefault();
    if (pwForm.newPassword.length < 8) {
      setPwMsg({ type: 'error', text: 'Password must be at least 8 characters.' }); return;
    }
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwMsg({ type: 'error', text: 'Passwords do not match.' }); return;
    }
    setPwSaving(true); setPwMsg({ type: '', text: '' });
    try {
      const token = localStorage.getItem('previu_token');
      const res = await fetch(`${API_BASE}/patient-auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ phone: patient.phone, newPassword: pwForm.newPassword }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to change password.');
      }
      setPwMsg({
        type: 'success',
        text: hasPassword === false
          ? 'Password set. You can now sign in with your phone and this password, '
            + 'or keep using the WhatsApp OTP.'
          : 'Password saved. You can now sign in with it.',
      });
      setPwForm({ newPassword: '', confirmPassword: '' });
      // They have one now, so the section stops offering to set a first password.
      setHasPassword(true);
    } catch (err) {
      setPwMsg({ type: 'error', text: err.message || 'Failed to change password.' });
    } finally { setPwSaving(false); }
  }

  // Scroll to the section the caller asked for.
  //
  // The portal's profile gate links here, and this page opens on Edit Profile
  // and Password — the Health Profile is several screens down. A new patient
  // told "complete your health profile", dropped at the top of a long settings
  // page, has no way to know that is what they are looking at.
  //
  // Keyed on a hash rather than page state so the destination survives a
  // reload and can be linked to from anywhere. Waiting for `addresses` to
  // resolve matters: the sections above Health Profile change height when the
  // address list loads, so scrolling before that lands in the wrong place.
  useEffect(() => {
    const target = (location.hash || '').replace('#', '');
    if (!target) return;
    // Safe to gate on: loadAddresses sets [] on failure as well as on success,
    // so this always resolves out of null and the scroll is never stranded by a
    // failed address fetch.
    if (addresses === null) return;      // layout above is still settling

    const el = document.getElementById(target);
    if (!el) return;

    // rAF so the scroll runs after this render has been painted.
    const raf = requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(raf);
  }, [location.hash, addresses]);

  return (
    <PatientAppShell breadcrumbs={[{ label: 'Settings' }]}>

      {/* Profile hero */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16,
        marginBottom: avatarMsg.text ? 8 : 28,
        background: 'linear-gradient(135deg, var(--brand-dark) 0%, var(--brand-navy) 100%)',
        borderRadius: 'var(--radius-lg)', padding: '22px 28px',
      }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%', overflow: 'hidden',
            background: avatarUrl ? 'transparent' : 'linear-gradient(135deg, var(--brand-accent), var(--amber-text))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, fontWeight: 800, color: 'var(--text-on-brand-dark)',
            boxShadow: '0 0 0 3px rgba(184,132,58,0.3)',
          }}>
            {avatarUrl
              ? <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : initials}
          </div>
          <button
            type="button"
            onClick={handleAvatarPick}
            disabled={avatarSaving}
            aria-label={avatarUrl ? 'Change photo' : 'Upload photo'}
            title={avatarUrl ? 'Change photo' : 'Upload photo'}
            style={{
              position: 'absolute', bottom: -2, right: -2,
              width: 22, height: 22, borderRadius: '50%',
              background: 'var(--brand-accent)', border: '2px solid var(--brand-dark)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: avatarSaving ? 'not-allowed' : 'pointer', padding: 0,
              opacity: avatarSaving ? 0.6 : 1,
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 11, color: 'var(--text-on-brand-dark)' }}>📷</span>
          </button>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleAvatarChange}
            style={{ display: 'none' }}
          />
        </div>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 20,
            fontWeight: 700, color: 'var(--text-on-brand-dark)', marginBottom: 3 }}>
            {patient?.firstName} {patient?.lastName}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
            {patient?.phone} {patient?.email ? `· ${patient.email}` : ''}
          </div>
          {/* The patient's own code, read-only — what they quote to their
              navigator. From GET /patients/{id} (PatientContext's profile);
              the login response's cached patient carries no code. */}
          {displayPatientCode(profile?.patientCode) && (
            <div className="settings-hero-code" data-testid="patient-code">
              Patient ID: {displayPatientCode(profile.patientCode)}
            </div>
          )}
          {avatarUrl && (
            <button
              type="button"
              onClick={handleAvatarRemove}
              disabled={avatarSaving}
              style={{
                marginTop: 4, background: 'none', border: 'none', padding: 0,
                fontSize: 11, color: 'rgba(255,255,255,0.55)',
                textDecoration: 'underline', cursor: avatarSaving ? 'not-allowed' : 'pointer',
              }}
            >
              Remove photo
            </button>
          )}
        </div>
      </div>
      {avatarMsg.text && (
        <div style={{
          padding: '8px 14px', borderRadius: 8, fontSize: 12.5, marginBottom: 20,
          background: avatarMsg.type === 'success' ? 'var(--green-bg)' : 'var(--red-bg)',
          color:      avatarMsg.type === 'success' ? 'var(--green)' : 'var(--red)',
          border:     `1px solid ${avatarMsg.type === 'success' ? 'var(--green)' : 'var(--red)'}`,
        }}>
          {avatarMsg.type === 'success' ? '✓ ' : '⚠️ '}{avatarMsg.text}
        </div>
      )}

      {/* ── Tabs ──
             This page used to be one long stack of every section — Profile,
             Ask Previu, Appearance, Password, Addresses, Health
             Profile, Symptom Log, Confounds — with no way to jump past what
             you're not here for. Grouped into tabs instead; Billing is a
             real navigation (not an in-page tab) since Wallet/Subscription/
             Invoices already exist as their own fully-built pages (see
             WalletBillingTabs.jsx) — reimplementing that content here would
             just duplicate it. The three in-page tabs stay mounted and are
             only display:none'd rather than unmounted, so the profile-gate's
             #health-profile/#symptom-log deep links can still scrollIntoView
             into them once the hash effect above switches to the right tab. */}
      <div className="tabs-bar" style={{ marginBottom: 24 }}>
        {SETTINGS_TABS.map(t => (
          <button
            key={t.key}
            type="button"
            className={`tab-btn${activeTab === t.key ? ' active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
        <NavLink to="/patient/wallet" className="tab-btn">
          Billing
        </NavLink>
      </div>

      <div style={{ display: activeTab === 'profile' ? 'block' : 'none' }}>
      {/* ── Edit Profile ── */}
      <Section title="✏️ Edit Profile">
        {profileMsg.text && (
          <div style={{
            padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16,
            background: profileMsg.type === 'success' ? 'var(--green-bg)' : 'var(--red-bg)',
            color:      profileMsg.type === 'success' ? 'var(--green)' : 'var(--red)',
            border:     `1px solid ${profileMsg.type === 'success' ? 'var(--green)' : 'var(--red)'}`,
          }}>
            {profileMsg.type === 'success' ? '✓ ' : '⚠️ '}{profileMsg.text}
          </div>
        )}
        <form onSubmit={handleUpdateProfile}>
          <div className="form-grid" style={{ gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>First Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input
                style={inputStyle()}
                type="text" value={profileForm.firstName}
                onChange={setProfile('firstName')}
                placeholder="First name" required
                onFocus={e => (e.target.style.borderColor = 'var(--brand-accent)')}
                onBlur={e  => (e.target.style.borderColor = 'var(--border)')}
              />
            </div>
            <div>
              <label style={labelStyle}>Last Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input
                style={inputStyle()}
                type="text" value={profileForm.lastName}
                onChange={setProfile('lastName')}
                placeholder="Last name" required
                onFocus={e => (e.target.style.borderColor = 'var(--brand-accent)')}
                onBlur={e  => (e.target.style.borderColor = 'var(--border)')}
              />
            </div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Email</label>
            <input
              style={inputStyle()}
              type="email" value={profileForm.email}
              onChange={setProfile('email')}
              placeholder="your@email.com"
              onFocus={e => (e.target.style.borderColor = 'var(--brand-accent)')}
              onBlur={e  => (e.target.style.borderColor = 'var(--border)')}
            />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Gender <span style={{ color: 'var(--red)' }}>*</span></label>
            <select
              className="form-select"
              style={inputStyle()}
              value={profileForm.gender}
              onChange={setProfile('gender')}
              required
              onFocus={e => (e.target.style.borderColor = 'var(--brand-accent)')}
              onBlur={e  => (e.target.style.borderColor = 'var(--border)')}
            >
              <option value="">Select gender</option>
              <option value="MALE">Male</option>
              <option value="FEMALE">Female</option>
              <option value="OTHER">Other</option>
            </select>
            {!profile?.gender && (
              <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>
                Missing from your profile — some health assessments rely on this. Please set it.
              </div>
            )}
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Phone</label>
            <input
              style={inputStyle({ opacity: 0.6 })}
              type="tel" value={patient?.phone || ''} disabled
              title="Phone number cannot be changed — it is your login ID"
            />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Phone number is your login ID and cannot be changed.
            </div>
          </div>
          <button
            type="submit"
            disabled={profileSaving}
            style={{
              padding: '11px 24px', borderRadius: 'var(--radius-md)',
              background: profileSaving ? 'var(--text-muted)' : 'var(--brand-accent)',
              border: 'none', color: 'var(--text-on-brand-dark)', fontWeight: 600, fontSize: 14,
              cursor: profileSaving ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            }}
          >
            {profileSaving ? 'Saving…' : '✓ Save Changes'}
          </button>
        </form>
      </Section>
      </div>

      <div style={{ display: activeTab === 'preferences' ? 'block' : 'none' }}>
      {/* ── Install on a phone ── */}
      <Section title="📲 Install the app">
        <InstallAppSection />
      </Section>

      {/* ── Notifications ──
             Channel preferences (account-wide, saved to the backend) first,
             then push for this device. One section rather than two: both are
             "how Previu reaches me", and two sections titled Notifications
             would read as a duplicate. */}
      <Section title="🔔 Notifications" id={NOTIFICATIONS_ANCHOR}>
        {notifError && <div className="alert alert-error">{notifError}</div>}
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.6 }}>
          Choose how Previu lets you know about appointments, results and messages.
        </div>
        <label className="form-check">
          <input type="checkbox" disabled={notifSaving}
            checked={notifPrefs.whatsappNotificationPreference}
            onChange={e => handleNotificationPreferenceChange('whatsappNotificationPreference', e.target.checked)} />
          WhatsApp messages
        </label>
        <label className="form-check">
          <input type="checkbox" disabled={notifSaving}
            checked={notifPrefs.appNotificationPreference}
            onChange={e => handleNotificationPreferenceChange('appNotificationPreference', e.target.checked)} />
          In-app (bell) notifications
        </label>
        <div className="form-hint" style={{ marginTop: 8, marginBottom: 20, lineHeight: 1.6 }}>
          Turning WhatsApp messages off does not stop login codes or important
          messages about your account — we will always send those.
        </div>
        <PushNotificationsSection />
      </Section>

      {/* ── Appearance (theme) ── */}
      <Section title="🎨 Appearance">
        {themeMsg.text && (
          <div style={{
            padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16,
            background: themeMsg.type === 'success' ? 'var(--green-bg)' : 'var(--red-bg)',
            color:      themeMsg.type === 'success' ? 'var(--green)' : 'var(--red)',
            border:     `1px solid ${themeMsg.type === 'success' ? 'var(--green)' : 'var(--red)'}`,
          }}>
            {themeMsg.type === 'success' ? '✓ ' : '⚠️ '}{themeMsg.text}
          </div>
        )}
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
          Choose how the Previu portal looks for you.
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {[
            { value: 'LIGHT', label: 'Light',
              desc: 'A bright, warm-toned portal.' },
            { value: 'DARK', label: 'Dark',
              desc: 'The same portal in a dark color palette.' },
          ].map(opt => {
            const selected = theme === opt.value;
            return (
              <div
                key={opt.value}
                className="card"
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                aria-disabled={themeSaving}
                onClick={() => handleThemeChange(opt.value)}
                onKeyDown={e => {
                  if (!themeSaving && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleThemeChange(opt.value);
                  }
                }}
                style={{
                  flex: '1 1 220px', marginBottom: 0,
                  cursor: themeSaving ? 'not-allowed' : 'pointer',
                  border: `2px solid ${selected ? 'var(--brand-accent)' : 'var(--border)'}`,
                  opacity: themeSaving && !selected ? 0.6 : 1,
                  transition: 'border-color 0.15s',
                }}
              >
                <div style={{
                  fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700,
                  color: 'var(--text-primary)', marginBottom: 6,
                }}>
                  {selected ? '✓ ' : ''}{opt.label}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  {opt.desc}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* ── Today screen layout ── */}
      <Section title="📅 Today screen">
        {todayViewMsg.text && (
          <div style={{
            padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16,
            background: todayViewMsg.type === 'success' ? 'var(--green-bg)' : 'var(--red-bg)',
            color:      todayViewMsg.type === 'success' ? 'var(--green)' : 'var(--red)',
            border:     `1px solid ${todayViewMsg.type === 'success' ? 'var(--green)' : 'var(--red)'}`,
          }}>
            {todayViewMsg.type === 'success' ? '✓ ' : '⚠️ '}{todayViewMsg.text}
          </div>
        )}
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
          Choose how much your Today screen shows.
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {[
            { value: 'METRIC', label: 'Metric-oriented',
              desc: 'Your health score chart, everything that needs attention, and your health chapters — all in one view.' },
            { value: 'SIMPLIFIED', label: 'Simplified',
              desc: 'Just the one thing to do next, with your health score and care plan progress at a glance.' },
          ].map(opt => {
            const selected = todayView === opt.value;
            return (
              <div
                key={opt.value}
                className="card"
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                aria-disabled={todayViewSaving}
                onClick={() => handleTodayViewChange(opt.value)}
                onKeyDown={e => {
                  if (!todayViewSaving && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleTodayViewChange(opt.value);
                  }
                }}
                style={{
                  flex: '1 1 220px', marginBottom: 0,
                  cursor: todayViewSaving ? 'not-allowed' : 'pointer',
                  border: `2px solid ${selected ? 'var(--brand-accent)' : 'var(--border)'}`,
                  opacity: todayViewSaving && !selected ? 0.6 : 1,
                  transition: 'border-color 0.15s',
                }}
              >
                <div style={{
                  fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700,
                  color: 'var(--text-primary)', marginBottom: 6,
                }}>
                  {selected ? '✓ ' : ''}{opt.label}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  {opt.desc}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* ── Language ──
             Last in Preferences so nothing above it moves. Each option names
             itself in its own script. English is titled in --font-display like
             the cards above; Malayalam and Hindi in --font-body, which carries
             the Noto fallbacks (--font-display does not). */}
      {preferredLanguage && !languageHidden && (
      <Section title="🌐 Language" id={LANGUAGE_ANCHOR}>
        {languageMsg.text && (
          <div style={{
            padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16,
            background: languageMsg.type === 'success' ? 'var(--green-bg)' : 'var(--red-bg)',
            color:      languageMsg.type === 'success' ? 'var(--green)' : 'var(--red)',
            border:     `1px solid ${languageMsg.type === 'success' ? 'var(--green)' : 'var(--red)'}`,
          }}>
            {languageMsg.type === 'success' ? '✓ ' : '⚠️ '}{languageMsg.text}
          </div>
        )}
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
          Choose the language you would like to use with Previu.
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {[
            { value: 'en', label: 'English',  desc: 'English' },
            { value: 'ml', label: 'മലയാളം',  desc: 'Malayalam' },
            { value: 'hi', label: 'हिन्दी',     desc: 'Hindi' },
          ].map(opt => {
            const selected = preferredLanguage === opt.value;
            return (
              <div
                key={opt.value}
                className="card"
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                aria-disabled={languageSaving}
                onClick={() => handleLanguageChange(opt.value)}
                onKeyDown={e => {
                  if (!languageSaving && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleLanguageChange(opt.value);
                  }
                }}
                style={{
                  flex: '1 1 160px', marginBottom: 0,
                  cursor: languageSaving ? 'not-allowed' : 'pointer',
                  border: `2px solid ${selected ? 'var(--brand-accent)' : 'var(--border)'}`,
                  opacity: languageSaving && !selected ? 0.6 : 1,
                  transition: 'border-color 0.15s',
                }}
              >
                <div lang={opt.value} style={{
                  fontFamily: opt.value === 'en' ? 'var(--font-display)' : 'var(--font-body)',
                  fontSize: 15, fontWeight: 700,
                  color: 'var(--text-primary)', marginBottom: 6,
                }}>
                  {selected ? '✓ ' : ''}{opt.label}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  {opt.desc}
                </div>
              </div>
            );
          })}
        </div>
      </Section>
      )}
      </div>

      <div style={{ display: activeTab === 'profile' ? 'block' : 'none' }}>
      {/* ── Password ──
             A patient who registered themselves has never had a password
             (RegisterPage collects no password field) and signs in by WhatsApp
             OTP, so for them this form sets a FIRST password rather than
             changing anything — a different thing to explain.
             GET /api/patient-auth/me tells the two apart; until it answers,
             hasPassword is null and the copy stays true for both. */}
      <Section title={hasPassword === false ? '🔑 Set a Password' : '🔑 Password'}>
        {pwMsg.text && (
          <div style={{
            padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16,
            background: pwMsg.type === 'success' ? 'var(--green-bg)' : 'var(--red-bg)',
            color:      pwMsg.type === 'success' ? 'var(--green)' : 'var(--red)',
            border:     `1px solid ${pwMsg.type === 'success' ? 'var(--green)' : 'var(--red)'}`,
          }}>
            {pwMsg.type === 'success' ? '✓ ' : '⚠️ '}{pwMsg.text}
          </div>
        )}
        <div style={{ fontSize: 13, color: 'var(--text-secondary)',
          fontFamily: 'var(--font-body)', marginBottom: 18, lineHeight: 1.6 }}>
          {hasPassword === false ? (
            <>
              You do not have a password yet — you have been signing in with a
              WhatsApp OTP. Set one here to use the <strong>Password</strong> tab
              on the login screen as well. The OTP keeps working either way, so
              this is an addition, not a replacement.
            </>
          ) : hasPassword === true ? (
            <>
              Change the password you sign in with. You will not be asked for your
              current one — you are already signed in. The WhatsApp OTP keeps
              working either way.
            </>
          ) : (
            <>
              Set or change the password you sign in with. If you have been signing
              in with a WhatsApp OTP, setting one here lets you use the Password
              tab on the login screen instead — the OTP keeps working either way.
            </>
          )}
        </div>
        <form onSubmit={handleChangePassword} style={{ maxWidth: 380 }}>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>New Password <span style={{ color: 'var(--red)' }}>*</span></label>
            <div style={{ position: 'relative' }}>
              <input
                style={inputStyle({ paddingRight: 56 })}
                type={showPw ? 'text' : 'password'}
                value={pwForm.newPassword}
                onChange={setPw('newPassword')}
                placeholder="Min 8 characters" required minLength={8}
                onFocus={e => (e.target.style.borderColor = 'var(--brand-accent)')}
                onBlur={e  => (e.target.style.borderColor = 'var(--border)')}
              />
              <button type="button" onClick={() => setShowPw(p => !p)}
                style={{
                  position: 'absolute', right: 12, top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-muted)', fontSize: 13, fontWeight: 500,
                }}>
                {showPw ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Confirm Password <span style={{ color: 'var(--red)' }}>*</span></label>
            <input
              style={inputStyle()}
              type={showPw ? 'text' : 'password'}
              value={pwForm.confirmPassword}
              onChange={setPw('confirmPassword')}
              placeholder="Repeat password" required
              onFocus={e => (e.target.style.borderColor = 'var(--brand-accent)')}
              onBlur={e  => (e.target.style.borderColor = 'var(--border)')}
            />
            {pwForm.confirmPassword && pwForm.newPassword !== pwForm.confirmPassword && (
              <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>
                Passwords do not match
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={pwSaving || (pwForm.confirmPassword && pwForm.newPassword !== pwForm.confirmPassword)}
            style={{
              padding: '11px 24px', borderRadius: 'var(--radius-md)',
              background: pwSaving ? 'var(--text-muted)' : 'var(--brand-dark)',
              border: 'none', color: 'var(--text-on-brand-dark)', fontWeight: 600, fontSize: 14,
              cursor: pwSaving ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            }}
          >
            {pwSaving
              ? 'Saving…'
              : hasPassword === false ? '🔑 Set Password' : '🔑 Save Password'}
          </button>
        </form>
      </Section>

      {/* ── Health Profile ── */}
      {/* ── Collection addresses ──
             The pincode on these rows decides which slots Thyrocare offers and
             where a phlebotomist is physically sent, so it is shown prominently
             rather than buried at the end of one run-together line. */}
      <Section title="📍 Collection Addresses">
        {addrMsg.text && (
          <div className={`alert ${addrMsg.type === 'success' ? 'alert-success' : 'alert-error'}`}
               style={{ marginBottom: 16 }}>
            {addrMsg.type === 'success' ? '✓ ' : '⚠️ '}{addrMsg.text}
          </div>
        )}

        <div style={{ fontSize: 13, color: 'var(--text-secondary)',
          fontFamily: 'var(--font-body)', marginBottom: 18, lineHeight: 1.6 }}>
          Where a phlebotomist comes to collect your blood sample. The default
          address is used unless you pick another one when booking.
        </div>

        {addresses === null ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading your addresses…</div>
        ) : addresses.length === 0 && editingAddrId !== 'new' ? (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
            You have no saved addresses yet. Add one so home collection can be booked.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18 }}>
            {addresses.map(a => (
              <div key={a.id} style={{
                border: `1px solid ${a.isDefault ? 'var(--brand-accent)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-md)',
                padding: '14px 16px',
                background: 'var(--bg-subtle)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
                  flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.04em',
                    color: 'var(--text-secondary)' }}>
                    {a.addressType || 'HOME'}
                  </span>
                  {a.isDefault && (
                    <span className="badge" style={{ fontSize: 11 }}>Default</span>
                  )}
                </div>
                <div style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.55 }}>
                  {a.street}
                  {a.landmark ? <>, near {a.landmark}</> : null}
                  <br />
                  {a.city}{a.city && a.state ? ', ' : ''}{a.state}
                </div>
                {/* Pincode on its own line, emphasised — it is the field that
                    decides slot availability and dispatch, and the one most
                    worth a patient double-checking. */}
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)',
                  marginTop: 6 }}>
                  Pincode {a.zipCode || '—'}
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn-sm btn-secondary"
                          onClick={() => startEditAddress(a)} disabled={addrSaving}>
                    Edit
                  </button>
                  {!a.isDefault && (
                    <button type="button" className="btn btn-sm btn-secondary"
                            onClick={() => handleMakeDefault(a)} disabled={addrSaving}>
                      Make default
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {editingAddrId === null ? (
          <button type="button" className="btn btn-secondary" onClick={startAddAddress}>
            + Add an address
          </button>
        ) : (
          <form onSubmit={handleSaveAddress} style={{ maxWidth: 520 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14,
              color: 'var(--text-primary)' }}>
              {editingAddrId === 'new' ? 'New address' : 'Edit address'}
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>
                House / flat and street <span style={{ color: 'var(--red)' }}>*</span>
              </label>
              <input style={inputStyle()} value={addrForm.street}
                     onChange={e => setAddrForm(f => ({ ...f, street: e.target.value }))}
                     placeholder="860, 10th Cross, RBI Layout, JP Nagar" required />
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 180px' }}>
                <label style={labelStyle}>City <span style={{ color: 'var(--red)' }}>*</span></label>
                <input style={inputStyle()} value={addrForm.city}
                       onChange={e => setAddrForm(f => ({ ...f, city: e.target.value }))}
                       placeholder="Bangalore" required />
              </div>
              <div style={{ flex: '1 1 180px' }}>
                <label style={labelStyle}>State <span style={{ color: 'var(--red)' }}>*</span></label>
                <input style={inputStyle()} value={addrForm.state}
                       onChange={e => setAddrForm(f => ({ ...f, state: e.target.value }))}
                       placeholder="Karnataka" required />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 160px' }}>
                <label style={labelStyle}>
                  Pincode <span style={{ color: 'var(--red)' }}>*</span>
                </label>
                <input style={inputStyle()} value={addrForm.zipCode} inputMode="numeric"
                       maxLength={6}
                       onChange={e => setAddrForm(f => ({
                         ...f, zipCode: e.target.value.replace(/\D/g, ''),
                       }))}
                       placeholder="560078" required />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Decides which collection slots are available.
                </div>
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label style={labelStyle}>Landmark</label>
                <input style={inputStyle()} value={addrForm.landmark}
                       onChange={e => setAddrForm(f => ({ ...f, landmark: e.target.value }))}
                       placeholder="Opposite the park (optional)" />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 160px' }}>
                <label style={labelStyle}>Type</label>
                <select style={inputStyle()} value={addrForm.addressType}
                        onChange={e => setAddrForm(f => ({ ...f, addressType: e.target.value }))}>
                  <option value="HOME">Home</option>
                  <option value="OFFICE">Office</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
                color: 'var(--text-secondary)', flex: '1 1 200px' }}>
                <input type="checkbox" checked={addrForm.isDefault}
                       onChange={e => setAddrForm(f => ({ ...f, isDefault: e.target.checked }))} />
                Use this as my default collection address
              </label>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" className="btn btn-primary" disabled={addrSaving}>
                {addrSaving ? 'Saving…' : '📍 Save Address'}
              </button>
              <button type="button" className="btn btn-secondary" disabled={addrSaving}
                      onClick={() => { setEditingAddrId(null); setAddrMsg({ type: '', text: '' }); }}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </Section>
      </div>

      <div style={{ display: activeTab === 'health' ? 'block' : 'none' }}>
      <Section id={HEALTH_PROFILE_ANCHOR} title="🩺 Health Profile">
        <div style={{ fontSize: 13, color: 'var(--text-secondary)',
          fontFamily: 'var(--font-body)', marginBottom: 20, lineHeight: 1.6 }}>
          Your conditions, family history, medications and allergies are used to personalise
          your triage thresholds and care recommendations. Updates take effect on your next report.
        </div>
        {healthLoading ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>
            Loading your health profile…
          </div>
        ) : (
          <HealthProfileForm
            patient={patient}
            initialData={healthData}
            saveLabel="✓ Save health profile"
            // Re-read the patient so the portal's profile gate sees the new
            // medicalHistory immediately. Without this the patient fills the
            // form, saves, and the gate is still up until they reload — which
            // reads as the form not having worked.
            onSave={() => refreshProfile()}
            compact
          />
        )}
      </Section>

      <Section id={SYMPTOM_LOG_ANCHOR} title="📋 Symptom Log">
        {/* profile, not patient: previu_patient (the login-response object
            cached in localStorage) is {id, firstName, lastName, phone,
            email} — it never carries gender. SymptomLogSection's
            isWoman = patient?.gender === 'FEMALE' check was therefore always
            false, so it only ever rendered the 18 non-women-only questions —
            for every patient, regardless of actual gender. A female patient
            who answered all 18 visible ones had nothing left on screen, but
            the profile gate (which already reads profile.gender, same as
            the Health Profile form above) correctly required 20 and never
            cleared — the exact "wall with no door" this file's own gate
            logic was written to prevent, just reintroduced via the wrong
            object at this one call site. */}
        <SymptomLogSection patient={profile} />
      </Section>

      {/* Deliberately NOT one of patientProfileCompleteness's gated anchors —
          unlike the Health Profile and Symptom Log above, a confound is
          situational (something that happened around a specific draw), not
          a fixed set of questions to complete once. Reporting one is always
          optional and must never hold up the portal's completeness gate. */}
      <Section id="confound-log" title="🌡️ Confounds">
        <PatientConfoundsTab patient={profile} source="PATIENT_SELF_SERVICE" />
      </Section>
      </div>

      <div id={PRIVACY_ANCHOR} style={{ display: activeTab === 'privacy' ? 'block' : 'none' }}>
        <PrivacyConsentSection patientId={patient?.id} />
      </div>

    </PatientAppShell>
  );
}
